import { createStyles, keyframes } from 'antd-style'

import { PREVIEW_MOCK_WIDTH } from './constants'

const spin = keyframes`
  to { transform: rotate(360deg); }
`

/**
 * Lift under a card that floats on the pane rather than sitting in it. Wide and
 * low-opacity: the card should read as raised, not as cut out. Shared by the
 * preview mock and by the done step's build panel, which is the same object
 * continuing after the workspace exists.
 */
export const FLOATING_CARD_SHADOW = `
  0 24px 64px -12px rgba(11, 20, 32, 0.09),
  0 8px 24px -8px rgba(11, 20, 32, 0.05)
`

/** Width of the form column, wide enough for the localised step labels. */
export const FORM_COLUMN_WIDTH = 400

/**
 * The screens with no second pane — the domain fork and the wait for the
 * verdict. They read as prose, so they keep a reading width rather than
 * stretching across the stage the aside has vacated.
 */
export const SOLO_COLUMN_WIDTH = 520

/**
 * Breathing room between the pane and the card inside it. Tight: the slab behind
 * it already insets the whole thing, so this is the second margin in a row and
 * every pixel of it comes off the model of the workspace.
 */
export const PREVIEW_PANE_PADDING = 12

/**
 * Head and body inset for the summary's two handoff cards. The same 8/10 the
 * option tiles use — two cards sit side by side in a 400px column, so AntD's
 * small-card default spends more on the frame than the four lines inside it.
 */
export const DONE_CARD_INSET = { padding: '8px 10px' }

/**
 * Puts a skeleton row's glyph and its line on one centre line.
 *
 * AntD lays the avatar and the text out as table cells and pins both to the
 * top, which is right for a three-line paragraph and wrong for the one-line
 * rows both panes are built from — the glyph ends up floating above its own
 * label. Shared as a string because the two panes are separate `createStyles`
 * calls and this is the same defect in both.
 */
export const SKELETON_ROW_CENTERED = `
  .ant-skeleton {
    display: flex;
    align-items: center;
  }

  .ant-skeleton-header {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
  }

  .ant-skeleton-section {
    display: block;
    flex: 1;
    min-width: 0;
    width: auto;
  }

  /* Antd's gap between a title and the lines under it is the list's own top
     margin, so with no title above it that margin is a stray offset — and the
     one thing left that could push the bars off the glyph's centre line. */
  .ant-skeleton-section > .ant-skeleton-paragraph:first-child {
    margin-block-start: 0;
  }
`

/**
 * Only what AntD cannot express on its own: the two-pane stage that fills the
 * viewport, the scaled preview, the spotlight ring, and the "selected" state for
 * option tiles (`Button` has no such state).
 */
export const useStyles = createStyles(({ token, css }) => ({
  /**
   * The flow's own column inside `PublicSplitShell`. It contributes no padding
   * and no height of its own — the shell owns both, which is the whole point of
   * sharing it with the sign-in screens.
   *
   * `Spin` in nested mode wraps its children in a root plus a `-container`;
   * left alone those collapse onto their content and the step's action stops
   * sitting on the column's bottom edge.
   */
  contentColumn: css`
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;

    > .ant-spin,
    > .ant-spin > .ant-spin-container {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  `,

  /**
   * The matching workspaces on the domain fork.
   *
   * One bordered box with divided rows, not a card each: a domain can match a
   * dozen workspaces, and a stack of separate cards reads as a dozen things to
   * compare when it is really one list to pick from.
   */
  joinList: css`
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: ${token.borderRadiusLG}px;
    overflow: hidden;
  `,

  joinRow: css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;

    & + & {
      border-block-start: 1px solid ${token.colorBorderSecondary};
    }

    &:hover {
      background: ${token.colorFillQuaternary};
    }
  `,

  joinName: css`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  `,

  /**
   * The two follow-up cards on the done step. CSS grid rather than AntD's
   * `Row`/`Col` for the same reason as the option tiles: that grid's gutter is
   * negative margins, which push past the pane's padding and hand the card a
   * horizontal scrollbar.
   */
  doneCardGrid: css`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;

    @media (max-width: 720px) {
      grid-template-columns: minmax(0, 1fr);
    }
  `,

  /**
   * The "recommended" mark beside a handoff card's title — a glyph, not a chip.
   * Two cards share a 400px column, so the word never had room: as a tag it
   * either pushed the title out of the head or truncated to "Recom…", which
   * says less than the icon does. The claim itself moves to the tooltip.
   */
  doneCardBadge: css`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    color: ${token.colorPrimary};
  `,

  /**
   * Equal-height cards whose body fills the leftover space, so the step count
   * can sit on the bottom edge however long the copy above it runs.
   */
  doneCard: css`
    height: 100%;
    display: flex;
    flex-direction: column;

    /* The theme pins a small card's header to 48px so it lines up with the
       hand-rolled table toolbars (see \`theme.ts\`) — there the header is a strip
       of controls. These two are tiles in a 400px column with four lines each,
       so the pin is what kept \`DONE_CARD_INSET\` from being visible at all;
       released here only, and the height follows the inset instead. */
    > .ant-card-head {
      min-height: 0;
    }

    .ant-card-body {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
  `,

  formBody: css`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 20px;
  `,

  /**
   * Takes the slack, so the step's fields stay at the top of the pane and the
   * action stays on its bottom edge. Scrolls internally once a step grows
   * taller than the card rather than pushing the action out of view.
   */
  formContent: css`
    flex: 0 1 auto;
    min-height: 0;
    /* Scrolling clips on both axes — CSS will not let one stay visible — which
       crops card shadows and focus rings flush against the pane's edges. Equal
       padding and negative margin widen the clipping box by the same amount
       they pull it back, so nothing inside moves a pixel. */
    padding: 8px;
    margin: -8px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
  `,

  /**
   * Right-hand slot of the address field. Always rendered, even while empty:
   * switching `suffix` between nothing and a node makes AntD swap the bare
   * input for an affix-wrapped one, which remounts the `<input>` and drops the
   * caret on the first keystroke.
   */
  availabilitySlot: css`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
  `,

  /**
   * Plain rotating glyph rather than `Spin`: a standalone `Spin` brings its own
   * positioning and lands mid-field instead of in the suffix slot.
   */
  availabilitySpinner: css`
    flex: 0 0 auto;
    color: ${token.colorTextTertiary};
    animation: ${spin} 0.8s linear infinite;
  `,

  /**
   * The animated wrapper around whichever step is on screen. Has to carry the
   * column layout itself: it sits between `formContent` and the step, and the
   * done screen pins its actions to the bottom with `margin-block-start: auto`.
   */
  stepPanel: css`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  `,

  /** Tinted tile behind a sign-in method's logo, same shape as the auth page. */
  methodIcon: css`
    width: 28px;
    height: 28px;
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: ${token.borderRadius}px;
  `,

  /**
   * Icon-only action sitting on the image's hover overlay. AntD's text-button
   * hover fill would paint a light plate over the dark overlay, and its 24px
   * box would push the pair of glyphs off-centre, so the button is stripped to
   * exactly its glyph. The focus outline is untouched.
   */
  coverAction: css`
    &&& {
      width: auto;
      min-width: auto;
      height: auto;
      padding: 0;
      line-height: 1;
    }

    &&&,
    &&&:hover,
    &&&:active {
      background: transparent;
      border-color: transparent;
    }
  `,

  /**
   * Company-size picker. AntD lays an option's icon and label out inline and
   * leaves them hugging the start of the block, which reads as ragged once
   * every option carries a glyph — this centres the pair inside its segment.
   *
   * Making the label a flex container also disables the baseline trick AntD
   * uses on a bare `<svg>`: it pairs `vertical-align: middle` with
   * `margin-block-end: 0.2em` to land the glyph on the cap-height centre.
   * `vertical-align` does nothing to a flex item, so only the margin survives
   * and rides the icon ~2.6px above the label that `align-items` just centred.
   * Zero it here rather than globally, so every inline Segmented keeps AntD's
   * correction.
   */
  sizeSegmented: css`
    &&& .ant-segmented-item-label {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
    }

    &&& .ant-segmented-item-icon {
      display: inline-flex;
      margin-inline-end: 0;
    }

    &&& .ant-segmented-item-icon > svg {
      margin-block-end: 0;
    }
  `,

  /** Uploaded workspace picture inside the logo block. */
  logoImage: css`
    border-radius: ${token.borderRadius}px;
    object-fit: cover;
  `,

  /** Same framed block the existing wizard uses for its logo picker. */
  logoBlock: css`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    border: 1px dashed ${token.colorBorder};
    border-radius: ${token.borderRadius}px;
    background: ${token.colorFillQuaternary};
  `,

  /**
   * Backdrop the mock floats on — the same white as the form half, so the card
   * reads as one surface with no seam down the middle.
   *
   * Fills whatever height the stage has; `PreviewPanel` scales the mock to fit
   * both axes, so the whole workspace — and with it every spotlight target —
   * stays on screen. The stage owns the rounded corners now, not this pane.
   *
   * No width gate of its own. It used to hide itself below 1180px while the
   * slab it sits on renders from 992px up (`PublicSplitShell`'s `screens.lg`),
   * so every viewport in between — a zoomed-in window is the ordinary way to
   * land there — got the dark slab with nothing inside it. Whether there is
   * room for a second pane at all is the shell's decision; if the slab is
   * there, so is the mock.
   */
  previewPane: css`
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${PREVIEW_PANE_PADDING}px;

    /* Transparent: the shell's dark slab showing through is a layer under the
       mock, not a reason for it to grow — the inset stays exactly as it was. */
    background: transparent;

    /* Every grey bar in the mock is a Skeleton.Input, and AntD's wrapper around
       it keeps its own default height — the bar we asked for is then pinned to
       the top of a taller box and sits above the avatar and the text it shares
       a row with. Collapsing the wrapper onto its child puts them back on one
       line. */
    .ant-skeleton-element {
      display: flex;
      align-items: center;
      line-height: 0;
    }

    ${SKELETON_ROW_CENTERED}
  `,

  /**
   * The mock's meta strip sits flush inside the hero card, so it keeps only the
   * rule separating it from the greeting above. Its own frame doubled up with
   * the card's border and rounded its corners inside a square well.
   */
  previewMeta: css`
    /* Beats AntD's own \`.ant-descriptions.ant-descriptions-bordered >
       .ant-descriptions-view\`, which outranks a plain descendant rule. */
    &&& > .ant-descriptions-view {
      border-radius: 0;
      border-block-end: none;
      border-inline: none;
    }

    /* The tinted label cell keeps its own corner radius otherwise, which reads
       as a notch now that the frame around it is square. */
    &&& .ant-descriptions-row > th:first-child,
    &&& .ant-descriptions-row > td:first-child {
      border-radius: 0;
    }
  `,

  /**
   * No border — on a white pane the shadow alone is what lifts it, and an
   * outline around a mock of the real UI reads as a frame around a screenshot.
   */
  previewCard: css`
    position: relative;
    flex: 0 0 auto;
    overflow: hidden;
    border: none;
    border-radius: ${token.borderRadiusLG}px;
    background: ${token.colorBgContainer};
    color: ${token.colorText};
    box-shadow: ${FLOATING_CARD_SHADOW};
  `,

  /**
   * Authored at a fixed width, then scaled to the card. The height is set by
   * `PreviewPanel` from whatever the pane leaves, so the mock fills the panel
   * instead of floating inside it. Never interactive.
   */
  previewStage: css`
    width: ${PREVIEW_MOCK_WIDTH}px;
    transform-origin: top left;
    pointer-events: none;
    user-select: none;
  `,

  ring: css`
    position: absolute;
    pointer-events: none;
    border: 2px solid ${token.colorPrimary};
    border-radius: ${token.borderRadiusLG}px;
    box-shadow: 0 0 0 6px ${token.colorPrimaryBg};
    transition: all 0.3s var(--ease-out);
  `,

  ringCaption: css`
    position: absolute;
    pointer-events: none;
    padding: 6px 10px;
    border-radius: ${token.borderRadius}px;
    background: ${token.colorPrimary};
    box-shadow: ${token.boxShadowTertiary};
    transition: all 0.3s var(--ease-out);
    white-space: nowrap;
  `,

  /**
   * Option tiles wrap rather than filling a fixed number of columns: the labels
   * differ in length by a lot between languages, so a column count decided in
   * advance either stretched short options across dead space or broke long ones
   * onto two lines while their neighbours sat half empty. Each tile takes the
   * width of its own label and the row breaks where it runs out.
   *
   * Flex rather than AntD's Row/Col: that gutter is implemented with negative
   * margins, which push past the card's padding and give the form a horizontal
   * scrollbar.
   */
  tileRow: css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  `,

  /** Option tile: left-aligned, wraps long labels instead of clipping them. */
  tile: css`
    &&& {
      height: auto;
      /* 8px above and below a 13px line lands the single-line tile on exactly
         32px. A minimum rather than a fixed height, so a label that wraps —
         several industries do in Russian — grows instead of being clipped. */
      min-height: 32px;
      padding: 8px 10px;
      justify-content: flex-start;
      text-align: left;
      white-space: normal;
      line-height: 1.25;
    }
  `,

  tileSelected: css`
    &&& {
      border-color: ${token.colorPrimary};
      background: ${token.colorPrimaryBg};
      color: ${token.colorPrimaryText};
    }
  `
}))
